import logger from "./logger";
import secureStorage from "./secureStorage";

// Common/utils/menuRights.ts
export interface MenuRights {
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  view?: boolean;
  export?: boolean;
  import?: boolean;
}

export interface MenuItem {
  id: string;
  label: string;
  link?: string;
  icon?: string;
  parentId?: string;
  rights?: MenuRights;
  subItems?: MenuItem[];
} 

/**
 * Recursively search for a menu item by its link/route
 * Supports exact match and prefix match for child routes (e.g., /postal/detail/123 matches /postal)
 */
export const findMenuItemByRoute = (
  menuData: MenuItem[],
  route: string
): MenuItem | null => {
  let bestMatch: MenuItem | null = null;
  let bestMatchLength = 0;

  const search = (items: MenuItem[]): MenuItem | null => {
    for (const item of items) {
      if (item.link) {
        // Exact match - return immediately
        if (item.link === route) {
          return item;
        }

        // Prefix match - keep track of best (longest) match
        // Route /postal/detail/123 should match /postal but not /post
        if (route.startsWith(item.link + '/') || route.startsWith(item.link + '?')) {
          if (item.link.length > bestMatchLength) {
            bestMatch = item;
            bestMatchLength = item.link.length;
          }
        }
      }

      // Recursively check sub-items
      if (item.subItems && item.subItems.length > 0) {
        const found: MenuItem | null = search(item.subItems);
        if (found && found.link === route) {
          return found; // Exact match found in sub-items
        }
      }
    }
    return null;
  };

  const exactMatch = search(menuData);
  return exactMatch || bestMatch;
};

/**
 * Route aliases — a route the user's browser can actually land on that has no
 * corresponding `rbac_modules.web_route` entry of its own, mapped to the route
 * that DOES carry the real permissions. Mirrors the backend's
 * `MODULE_CODE_ALIASES` pattern (`enterprise-rbac.guard.ts`) for the same class
 * of problem: a stored route/module-code that drifted from what the app
 * actually serves.
 *
 * Found via: the sidebar's "Influencer Network" link is `/loyalty/influencers`
 * (module_id 168, `canAdd`/`canEdit`/etc. all true) — but that route's own page
 * component is a client-side `<Navigate to="/customer/influencer-network" />`
 * stub (`departments/loyalty/pages/Influencers/InfluencerMaster.tsx`), so by
 * the time the user is actually looking at the list, `location.pathname` has
 * changed to `/customer/influencer-network`, which has no menu entry of its
 * own — `findMenuItemByRoute` returns nothing, rights resolve to `{}`, and
 * Add/Import/Export all silently disappear even though the permission data
 * says the user is allowed. Confirmed identical on every tenant sharing this
 * DB (same `module_id 168`/route for every org) — not tenant-specific.
 */
export const ROUTE_ALIASES: Record<string, string> = {
  '/customer/influencer-network': '/loyalty/influencers',
};

/**
 * Get rights for a specific route from menu data
 */
export const getRouteRights = (route: string): MenuRights => {
  try {
    const menuData = secureStorage.getMenuData();


    if (!menuData) {
      return {}; // No menu data, return empty rights
    }

    let menuItem = findMenuItemByRoute(menuData, route);

    if (!menuItem) {
      const aliasedRoute = ROUTE_ALIASES[route];
      if (aliasedRoute) {
        menuItem = findMenuItemByRoute(menuData, aliasedRoute);
      }
    }

    return menuItem?.rights || {};
  } catch (error) {
    logger.error("Error getting route rights:", error);
    return {};
  }
};

/**
 * Check if user has a specific right for current route
 */
export const hasRight = (route: string, right: keyof MenuRights): boolean => {
  const rights = getRouteRights(route);
  return rights[right] === true;
};